import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Fuel, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(identifier, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex">
      <div className="hidden lg:flex flex-col justify-between w-[45%] p-12 relative overflow-hidden border-r border-[#1F2937]">
        <img
          src="https://images.pexels.com/photos/34636185/pexels-photo-34636185.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Fuel station"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Fuel className="w-6 h-6 text-black" />
          </div>
          <span className="font-[Outfit] font-bold text-2xl text-white">Fuel Tracer</span>
        </div>
        <div className="relative">
          <h1 className="font-[Outfit] font-bold text-4xl xl:text-5xl text-white leading-tight tracking-tight">
            Every liter.<br />Every ringgit.<br />
            <span className="text-emerald-400">Fully reconciled.</span>
          </h1>
          <p className="text-[#9CA3AF] mt-4 max-w-md">
            Track daily meter readings, tanker deliveries and cash across all your stations — with automated monthly P&amp;L.
          </p>
        </div>
        <div className="relative text-xs font-mono uppercase tracking-widest text-emerald-400">
          Multi-station · MYR · Liters
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm animate-fadeUp" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Fuel className="w-6 h-6 text-black" />
            </div>
            <span className="font-[Outfit] font-bold text-2xl text-white">Fuel Tracer</span>
          </div>
          <h2 className="font-[Outfit] font-bold text-2xl text-white mb-1">Sign in</h2>
          <p className="text-[#9CA3AF] text-sm mb-6">Use your email or username to continue.</p>

          {error && (
            <div className="mb-4 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="identifier" className="text-[#9CA3AF]">Email or username</Label>
              <Input
                id="identifier"
                data-testid="login-identifier-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Email or username"
                className="mt-1.5 bg-[#111827] border-[#374151] text-white"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#9CA3AF]">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="mt-1.5 bg-[#111827] border-[#374151] text-white"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full mt-6 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm text-emerald-400 hover:underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
